import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { loadRecentSearches, normalizeSearchQuery, saveRecentSearch } from '../utils/searchUtils';

function flattenResults(data) {
  const suggestions = (data?.suggestions || []).map((q) => ({ type: 'suggestion', key: `s:${q}`, label: q, query: q }));
  const categories = (data?.categories || []).map((c) => ({
    type: 'category',
    key: `c:${c.slug || c._id}`,
    label: c.name,
    to: `/products?category=${encodeURIComponent(c.slug || c._id)}`,
  }));
  const products = (data?.products || []).map((p) => ({
    type: 'product',
    key: `p:${p._id}`,
    label: p.name,
    to: `/product/${p._id}`,
    price: typeof p.price === 'number' ? p.price : null,
    image: p.images?.main,
  }));
  return { suggestions, categories, products };
}

export default function SearchAutocomplete({ value, onValueChange, onRequestClose, inputRef, searchFocused = false }) {
  const navigate = useNavigate();
  const listboxId = useId();
  const containerRef = useRef(null);
  const abortRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState(() => loadRecentSearches());

  const query = useMemo(() => normalizeSearchQuery(value), [value]);

  const { suggestions, categories, products } = useMemo(() => flattenResults(data), [data]);

  const sections = useMemo(() => {
    const recentItems =
      query.length === 0
        ? recent.map((q) => ({ type: 'recent', key: `r:${q}`, label: q, query: q }))
        : [];

    return {
      recent: recentItems,
      suggestions,
      categories,
      products,
    };
  }, [recent, query, suggestions, categories, products]);

  const flatOptions = useMemo(() => {
    const out = [];
    if (sections.recent.length) out.push(...sections.recent);
    if (sections.suggestions.length) out.push(...sections.suggestions);
    if (sections.categories.length) out.push(...sections.categories);
    if (sections.products.length) out.push(...sections.products);
    return out;
  }, [sections]);

  useEffect(() => {
    if (query.length === 0) {
      setData(null);
      setIsOpen(searchFocused && recent.length > 0);
      setIsLoading(false);
      setActiveIndex(-1);
      return;
    }

    if (query.length < 2) {
      setData(null);
      setIsOpen(false);
      setIsLoading(false);
      setActiveIndex(-1);
      return;
    }

    setIsOpen(true);
    setIsLoading(true);
    setActiveIndex(-1);

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/products/autocomplete?q=${encodeURIComponent(query)}`, {
          signal: controller.signal,
        });
        setData(res.data);
      } catch (e) {
        if (e?.name !== 'CanceledError' && e?.code !== 'ERR_CANCELED') {
          setData(null);
        }
      } finally {
        setIsLoading(false);
      }
    }, 150);

    return () => clearTimeout(t);
  }, [query, recent.length, searchFocused]);

  // Close on click outside / escape
  useEffect(() => {
    if (!isOpen) return;
    const onDocMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setActiveIndex(-1);
        onRequestClose?.();
      }
    };
    const onDocKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setActiveIndex(-1);
        onRequestClose?.();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onDocKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onDocKeyDown);
    };
  }, [isOpen, onRequestClose]);

  const commitSearch = (q) => {
    const next = normalizeSearchQuery(q);
    if (!next) return;
    onValueChange?.(next);
    saveRecentSearch(next);
    setRecent(loadRecentSearches());
    setIsOpen(false);
    setActiveIndex(-1);
    onRequestClose?.();
    navigate(`/products?search=${encodeURIComponent(next)}`);
  };

  const onKeyDown = (e) => {
    if (!isOpen) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(flatOptions.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(-1, i - 1));
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && flatOptions[activeIndex]) {
        e.preventDefault();
        const opt = flatOptions[activeIndex];
        if (opt.type === 'suggestion' || opt.type === 'recent') {
          commitSearch(opt.query);
        } else if (opt.to) {
          setIsOpen(false);
          onRequestClose?.();
          navigate(opt.to);
        }
      } else {
        // Submit current query
        commitSearch(query);
      }
    }
  };

  useEffect(() => {
    const el = inputRef?.current;
    if (!el) return;
    el.addEventListener('keydown', onKeyDown);
    return () => el.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, flatOptions, activeIndex, query]);

  if (!isOpen) return null;

  const hasAny =
    sections.recent.length || sections.suggestions.length || sections.categories.length || sections.products.length;

  return (
    <div className="search-ac-container" ref={containerRef}>
      <div className="search-ac-panel" role="listbox" id={listboxId} aria-label="Search suggestions">
        <div className="search-ac-status" aria-live="polite">
          {isLoading ? 'Loading' : ''}
        </div>

        {!hasAny && !isLoading && (
          <div className="search-ac-empty" role="option" aria-selected="false">
            No results
          </div>
        )}

        {sections.recent.length > 0 && (
          <div className="search-ac-section" aria-label="Recent searches">
            <div className="search-ac-section-title">Recent</div>
            {sections.recent.map((opt) => (
              <button
                key={opt.key}
                type="button"
                id={`opt-${opt.key}`}
                role="option"
                aria-selected={flatOptions[activeIndex]?.key === opt.key}
                className={`search-ac-item ${flatOptions[activeIndex]?.key === opt.key ? 'is-active' : ''}`}
                onClick={() => commitSearch(opt.query)}
              >
                <span className="search-ac-item-label">{opt.label}</span>
              </button>
            ))}
          </div>
        )}

        {sections.suggestions.length > 0 && (
          <div className="search-ac-section" aria-label="Suggestions">
            <div className="search-ac-section-title">Suggestions</div>
            {sections.suggestions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                id={`opt-${opt.key}`}
                role="option"
                aria-selected={flatOptions[activeIndex]?.key === opt.key}
                className={`search-ac-item ${flatOptions[activeIndex]?.key === opt.key ? 'is-active' : ''}`}
                onClick={() => commitSearch(opt.query)}
              >
                <span className="search-ac-item-label">{opt.label}</span>
              </button>
            ))}
          </div>
        )}

        {sections.categories.length > 0 && (
          <div className="search-ac-section" aria-label="Categories">
            <div className="search-ac-section-title">Categories</div>
            {sections.categories.map((opt) => (
              <button
                key={opt.key}
                id={`opt-${opt.key}`}
                type="button"
                role="option"
                aria-selected={flatOptions[activeIndex]?.key === opt.key}
                className={`search-ac-item search-ac-link ${flatOptions[activeIndex]?.key === opt.key ? 'is-active' : ''}`}
                onClick={(e) => {
                  // Prevent any parent <form> submission on mobile.
                  e.preventDefault();
                  onRequestClose?.();
                  navigate(opt.to);
                }}
              >
                <span className="search-ac-item-label">{opt.label}</span>
              </button>
            ))}
          </div>
        )}

        {sections.products.length > 0 && (
          <div className="search-ac-section" aria-label="Products">
            <div className="search-ac-section-title">Products</div>
            {sections.products.map((opt) => (
              <button
                key={opt.key}
                id={`opt-${opt.key}`}
                role="option"
                aria-selected={flatOptions[activeIndex]?.key === opt.key}
                className={`search-ac-item search-ac-link ${flatOptions[activeIndex]?.key === opt.key ? 'is-active' : ''}`}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  onRequestClose?.();
                  navigate(opt.to);
                }}
              >
                <span className="search-ac-product">
                  <span className="search-ac-product-name">{opt.label}</span>
                  {opt.price != null && <span className="search-ac-product-price">£{opt.price.toFixed(2)}</span>}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

